/* ============================================================
   Das Wörterbuch von P6 (SPEC §13.7): Modell, Import, Zuordnen. Hängt in
   `lib/texte/index.ts` als eigener Ast unter `t.modell`; der Name des Exports ist
   Vertrag.

   Wortlaut aus dem Design-Canvas (Modell, Zuordnen, ImportBericht und die drei
   Mobil-Artboards, E10) und aus §8/§9. Tonlage nach .impeccable.md: ruhig, präzise,
   Fachwörter bleiben (GlobalId, Pset, Qto, VW-Klasse, Typgruppe); die Oberfläche
   spricht niemanden an („Filter lösen“, nicht „Löse den Filter“).

   Zahlen kommen fertig formatiert herein (lib/format.ts: `zahl`), damit hier nur
   Sätze stehen und keine Rechnerei. Kein leerer Satz (lib/texte.test.ts).
   ============================================================ */

/** „1 Element“ / „12 Elemente“ — die Mehrzahl hängt an der Zahl, nicht am Text. */
const elemente = (n: number, z: string) => (n === 1 ? `${z} Element` : `${z} Elemente`);

export const modell = {
  titel: "Modell",
  titelZuordnen: "Zuordnen",
  titelImporte: "Importe",

  reiter: {
    elemente: "Elemente",
    zuordnen: "Zuordnen",
    offen: (z: string) => `${z} offen`,
  },

  kopf: {
    import: (datum: string, datei: string) => `Import ${datum} · ${datei}`,
    /** Mit Planstand (§19.9-2, A3): der Planstand vorn, das Hochladedatum hinten. */
    importPlanstand: (planstand: string, datei: string, datum: string) => `Planstand ${planstand} · ${datei} · Import ${datum}`,
    importWaehlen: "Import wählen",
    bericht: "Bericht",
    katalogWaehlen: "Katalog wählen",
  },

  leer: {
    keinImport: "Noch kein Modell importiert",
    keinImportSub: "Ein IFC aus Vectorworks hochladen; danach stehen hier Baum, Elementtabelle und Eigenschaften.",
    hochladen: "IFC hochladen",
    laeuft: "Der Import läuft noch",
    laeuftSub: "Sobald er fertig ist, erscheint das Modell hier von selbst.",
    gescheitert: "Dieser Import ist fehlgeschlagen",
    abgebrochen: "Dieser Import wurde abgebrochen",
    zumAktuellen: "Aktuellen Import öffnen",
    importFehler: "Der Import ließ sich nicht laden.",
    importFehlerSub: "Vielleicht wurde er gelöscht. Die Importliste zeigt, was es gibt.",
    zuDenImporten: "Zu den Importen",
    projektFehlt: "Dieses Projekt gibt es nicht (mehr).",
  },

  baum: {
    titel: "Baum",
    gruppierung: "Gruppierung",
    gruppen: {
      raum: "Raum",
      klasse: "Klasse",
      typ: "Typ",
      vw: "VW-Klasse",
      kategorie: "Kategorie",
    } as Record<string, string>,
    suchen: "Knoten suchen",
    sucheOeffnen: "Im Baum suchen",
    sucheSchliessen: "Suche schließen",
    hilfe: "Ein Klick hier oder in der Tabelle wählt dasselbe aus. Der Balken zeigt, wie weit die Elemente des Knotens zugeordnet sind.",
    mehr: "weitere Elemente — über die Tabelle filtern",
    leer: "Keine Knoten.",
    keinTreffer: "Kein Knoten passt.",
    aufklappen: "Aufklappen",
    zuklappen: "Zuklappen",
    ohneName: "(ohne Name)",
    fehler: "Der Baum ließ sich nicht laden.",
  },

  tabelle: {
    titel: "Elemente",
    suche: "Name, GlobalId, Typ, VW-Klasse",
    zaehler: (gesamt: string) => `${gesamt} Elemente`,
    gefiltert: (n: string) => `${n} gefiltert`,
    ausgewaehlt: (n: string) => `${n} ausgewählt`,
    filter: "Filter",
    spalten: "Spalten",
    csv: "CSV",
    csvTitel: "Sichtbare Tabelle als CSV (Semikolon, für Excel)",
    alleWaehlen: "Alle im Filter wählen",
    auswahlLeeren: "Auswahl aufheben",
    zeileWaehlen: "Zeile wählen",
    leer: "Kein Element passt zu diesem Filter.",
    fehler: "Die Tabelle ließ sich nicht laden.",
    sortieren: (spalte: string) => `Nach ${spalte} sortieren`,
    ausgefiltert: (n: number, z: string) =>
      n === 1 ? `${z} gewähltes Element ist ausgefiltert` : `${z} gewählte Elemente sind ausgefiltert`,
    filterLoesen: "Filter lösen",
    spaltenZuruecksetzen: "Spalten zurücksetzen",
    faerbung: "Färbung",
    faerbungZuordnung: "Zuordnung",
    faerbungKategorie: "Kategorie",
    ja: "ja",
    nein: "nein",
  },

  spalten: {
    titel: "Spalten",
    suche: "Spalte suchen",
    fest: "Grunddaten",
    psets: "Psets",
    qtos: "Qtos",
    vorgabe: "Vorgabe wiederherstellen",
    zuViele: (n: number) => `Höchstens ${n} Spalten auf einmal.`,
    hilfsPset: "VW-Hilfspset",
    namen: {
      global_id: "GlobalId",
      ifc_klasse: "Klasse",
      predefined_type: "PredefinedType",
      name: "Name",
      beschreibung: "Beschreibung",
      objekttyp: "Objekttyp",
      tag: "Tag",
      typ_name: "Typname",
      typ_klasse: "Typklasse",
      vw_klasse: "VW-Klasse",
      geschoss: "Geschoss",
      x: "x (m)",
      y: "y (m)",
      z: "z (m)",
      hat_geometrie: "Geometrie",
      zuordnung: "Zuordnung",
      kategorie: "Kategorie",
      karte: "Karte",
      material: "Material",
    } as Record<string, string>,
  },

  filter: {
    titel: "Filter",
    feld: "Feld",
    operator: "Operator",
    wert: "Wert",
    werteVorschlag: "Häufige Werte",
    hinzufuegen: "Filter setzen",
    entfernen: "Filter entfernen",
    alleEntfernen: "Alle Filter entfernen",
    zuViele: "Höchstens 25 Filter auf einmal.",
    operatoren: {
      "=": "=",
      "!=": "≠",
      "<": "<",
      "<=": "≤",
      ">": ">",
      ">=": "≥",
      enthaelt: "enthält",
      beginnt: "beginnt mit",
      existiert: "vorhanden",
      fehlt: "fehlt",
    } as Record<string, string>,
  },

  zustand: {
    bestaetigt: "bestätigt",
    bestaetigtLang: "bestätigt mit Karte",
    kategorie: "nur Kategorie",
    vorschlag: "Vorschlag",
    offen: "offen",
    ignoriert: "ignoriert",
    legende: "Legende der Zuordnungsfarben",
    legendeKategorie: "Legende der Kategorien",
    ohneKategorie: "ohne Kategorie",
  } as Record<string, string>,

  kategorien: {
    BEL: "Bodenbeläge",
    EIN: "Einfassungen",
    MOB: "Einbauten/Sitzmöbel",
    SPI: "Spielgeräte",
    VEG: "Vegetation",
  } as Record<string, string>,

  quelle: {
    kennung: "Kennung",
    hersteller: "Hersteller",
    regel: "Regel",
    vw_klasse: "VW",
    llm: "KI",
    ki: "KI",
    hand: "Hand",
    // §19.3: Stufe „vorgaenger" (C7) und „aufbau" (C5); dieselben Wörter wie in der Zuordnen-Liste.
    vorgaenger: "Planstand",
    aufbau: "Aufbau",
  } as Record<string, string>,

  auswahl: {
    anzahl: (n: number, z: string) => elemente(n, z),
    kategorieZuweisen: "Kategorie zuweisen",
    produkt: "Produkt …",
    karte: "Karte …",
    ignorieren: "Ignorieren",
    imKatalog: "Im Katalog zeigen",
    imKatalogNicht: "Die Auswahl hängt nicht an genau einer Karte.",
    schliessen: "Auswahl aufheben",
    zugeordnet: (n: number, z: string) => `${elemente(n, z)} zugeordnet.`,
    ignoriert: (n: number, z: string) => `${elemente(n, z)} ignoriert.`,
    gekappt: (z: string) => `nur die ersten ${z}`,
    gekapptHilfe: "Mehr Elemente fasst eine Auswahl nicht. Ganze Typgruppen ordnet der Reiter „Zuordnen“ zu.",
  },

  eigenschaften: {
    titel: "Eigenschaften",
    nichtsGewaehlt: "Kein Element gewählt",
    nichtsGewaehltSub: "Ein Element in Baum oder Tabelle wählen, um alles zu sehen, was das Modell darüber weiß.",
    vonGesamt: (i: string, n: string) => `${i} von ${n}`,
    vorheriges: "Vorheriges der Auswahl",
    naechstes: "Nächstes der Auswahl",
    keineAngabe: "keine Angabe im Modell",
    kopieren: "Kopieren",
    kopiert: "Kopiert",
    fehler: "Das Element ließ sich nicht laden.",
    bloecke: {
      kopf: "Element",
      zuordnung: "Zuordnung",
      lage: "Lage",
      typ: "Typ",
      ebenen: "Ebenen",
      psets: "Psets",
      qtos: "Qtos",
      materialien: "Materialien",
      klassifikation: "Klassifikation",
    },
    felder: {
      name: "Name",
      klasse: "Klasse",
      globalId: "GlobalId",
      tag: "Tag",
      beschreibung: "Beschreibung",
      objekttyp: "Objekttyp",
      raumpfad: "Raumpfad",
      geschoss: "Geschoss",
      lage: "x / y / z",
      quader: "Hüllquader",
      ohneGeometrie: "ohne Geometrie",
      quaderNichtBerechnet: "Quader nicht berechnet",
      eltern: "Eltern",
      typName: "Typname",
      typKlasse: "Typklasse",
      typGlobalId: "Typ-GlobalId",
      vwKlasse: "VW-Klasse",
      ebenenRoh: "Ebenen im Modell",
      system: "System",
      kennung: "Kennung",
      klassName: "Name",
      ohneSatz: "Einzelmaterialien",
    },
    hilfsPset: "VW-Hilfspset",
    raumKnotenWaehlen: (name: string) => `Alle Elemente in „${name}“ wählen`,
    elternWaehlen: "Elternelement wählen",
    bereich: (min: string, max: string) => `${min} … ${max}`,
  },

  zuordnung: {
    titel: (zustand: string) => `Zuordnung · ${zustand}`,
    keine: "Noch nicht zugeordnet.",
    ueberGruppe: "über die Typgruppe",
    eigene: "nur dieses Element",
    kategorie: "Kategorie",
    produkt: "Produkt",
    karte: "Karte",
    karteHier: "Karte in diesem Katalog",
    keineKarteHier: "in diesem Katalog noch ohne Karte",
    keinProdukt: "kein Produkt — nur Kategorie",
    begruendung: "Begründung",
    bestaetigen: "Bestätigen",
    aendern: "Ändern …",
    nurElement: "Nur dieses Element …",
    zuruecksetzen: "Zurücksetzen",
    zurueckgesetzt: "Zuordnung zurückgesetzt.",
    zielGruppe: (n: number | null, z: string) => (n === null ? "gilt für die ganze Typgruppe" : `gilt für die ganze Typgruppe · ${elemente(n, z)}`),
    gruppeIgnoriert: (n: number | null, z: string) => (n === null ? "Typgruppe ignoriert." : `Typgruppe ignoriert (${elemente(n, z)}).`),
    bestaetigt: "Bestätigt.",
    gesetztVon: (wer: string, wann: string) => `${wer} · ${wann}`,
  },

  zuordnen: {
    titel: "Typgruppen zuordnen",
    zaehler: (g: string, e: string) => `${g} Gruppen · ${e} Elemente`,
    alleBestaetigen: "Alle Vorschläge ≥ 0,9 bestätigen",
    alleBestaetigenMobil: "Alle ≥ 0,9 bestätigen",
    alleBestaetigt: (n: number, z: string) => (n === 1 ? `${z} Gruppe bestätigt.` : `${z} Gruppen bestätigt.`),
    filter: {
      alle: "Alle",
      offen: "Offen",
      vorschlag: "Vorschläge",
      bestaetigt: "Bestätigt",
      ignoriert: "Ignoriert",
    } as Record<string, string>,
    alleQuellen: "Alle Quellen",
    alleKategorien: "Alle Kategorien",
    suche: "Gruppe suchen",
    spalten: {
      gruppe: "Gruppe",
      menge: "Menge",
      vorschlag: "Vorschlag",
      zustand: "Zustand",
      aktion: "Aktion",
    },
    ohneKatalogbezug: "Ohne Katalogbezug",
    ohneKatalogbezugHilfe: "Referenzen, Haustechnik, Medien, Gebäude, Gelände — ohne Vorschlag, von Hand zuordenbar.",
    bestaetigen: "Bestätigen",
    karteAnlegen: "Karte anlegen",
    produkt: "Produkt …",
    karte: "Karte …",
    kategorie: "Kategorie",
    ignorieren: "Ignorieren",
    /** Weg in die Elementliste. Früher „Elemente mit Psets" — auch bei Gruppen ganz ohne
     *  Psets (Prüfung Runde 2). */
    elementeZeigen: (z: string, n: number) => (n === 1 ? `${z} Element zeigen` : `${z} Elemente zeigen`),
    auswahlZaehler: (g: number, gz: string, ez: string) => `${gz} ${g === 1 ? "Gruppe" : "Gruppen"} · ${ez} Elemente`,
    vorschlaegeBestaetigen: "Vorschläge bestätigen",
    katalogFehlt: "Für Produkte und Karten zuerst einen Katalog wählen.",
    teilGescheitert: (n: number, z: string, grund: string) =>
      `${n === 1 ? `${z} Gruppe ließ` : `${z} Gruppen ließen`} sich nicht bestätigen: ${grund}`,
    keinKatalogImProjekt: "Das Projekt hat noch keinen Katalog. Kategorien lassen sich trotzdem bestätigen.",
    leer: "Keine Typgruppe in dieser Auswahl.",
    fehler: "Die Typgruppen ließen sich nicht laden.",
    gruppeOhneName: "(ohne Name)",
    stueck: (z: string) => `${z} Stück`,
    flaeche: (z: string) => `${z} m²`,
    laenge: (z: string) => `${z} m`,
    gruppeWaehlen: "Gruppe wählen",
  },

  dialoge: {
    kategorieTitel: "Kategorie zuweisen",
    produktTitel: "Produkt wählen",
    nurKategorie: "Nur Kategorie",
    zurBibliothek: "Zur Bibliothek",
    karteTitel: "Karte wählen",
    karteSuche: "Karte suchen",
    karteKategorie: "Kategorie der Zuordnung",
    karteLeer: "Dieser Katalog hat noch keine Karten.",
    katalog: "Katalog",
    kategorie: "Kategorie",
    kategorieWaehlen: "Kategorie wählen",
    uebernehmen: "Übernehmen",
    zielGruppen: (n: number, z: string) => (n === 1 ? `für ${z} Typgruppe` : `für ${z} Typgruppen`),
    zielElemente: (n: number, z: string) => `für ${elemente(n, z)}`,
  },

  mobil: {
    titelModell: (projekt: string) => `${projekt} · Modell`,
    titelZuordnen: (projekt: string) => `${projekt} · Zuordnen`,
    von: (n: string, gesamt: string) => `${n} von ${gesamt}`,
    filterBlatt: "Filter",
    fertig: "Fertig",
    aendern: "Ändern",
  },

  import: {
    hochladen: "IFC hochladen",
    hochladenHilfe: (mb: string) => `IFC oder IFCZIP aus Vectorworks, bis ${mb} MB`,
    ablegen: "Datei hier ablegen",
    wirdHochgeladen: "Wird hochgeladen",
    hochgeladen: "Hochgeladen. Der Import beginnt gleich.",
    zuGross: (mb: string) => `Die Datei ist größer als ${mb} MB.`,
    falscheEndung: "Nur .ifc oder .ifczip.",
    abbrechen: "Abbrechen",
    abgebrochen: "Abgebrochen.",
    laeuft: "Läuft",
    wartetSatz: "Wartet, bis der laufende Import im Büro fertig ist.",
    fehlgeschlagen: "Fehlgeschlagen",
    fertig: "Fertig",
    aktuell: "aktuell",
    fehler: "Fehler",
    abgebrochenChip: "abgebrochen",
    gestartet: (zeit: string, wer: string) => (wer ? `gestartet ${zeit} von ${wer}` : `gestartet ${zeit}`),
    elementeAnzahl: (z: string) => `${z} Elemente`,
    planstand: (d: string) => `Planstand ${d}`,
    hochgeladenAm: (d: string) => `hochgeladen ${d}`,
    editorBleibt: "Editor und Seiten bleiben währenddessen bedienbar.",
    loeschen: "Import löschen",
    loeschenFrage: "Diesen Import löschen? Elemente und Typgruppen gehen mit; bestätigte Zuordnungen bleiben.",
    geloescht: "Import gelöscht.",
    bericht: "Bericht",
    datei: "IFC herunterladen",
    keine: "Noch keine Importe in diesem Projekt.",
    schritte: {
      laden: "Datei holen",
      lesen: "IFC lesen",
      speichern: "Elemente speichern",
      vergleichen: "Mit Vorgänger vergleichen",
      vorschlagen: "Vorschläge",
    } as Record<string, string>,
  },

  bericht: {
    titel: (datum: string) => `Bericht · Import ${datum}`,
    modellOeffnen: "Modell öffnen",
    zuordnen: "Zuordnen",
    ganz: "Ganzen Bericht ansehen",
    kpi: {
      elemente: "Elemente",
      typgruppen: "Typgruppen",
      gruppenKurz: "Gruppen",
      ohnePset: "ohne Pset",
      ohneGeometrie: "ohne Geometrie",
      ohneVw: "ohne VW-Klasse",
      globalId: "GlobalId erhalten",
      globalIdKurz: "GlobalId",
    },
    kurzWarnung: (pset: string, geo: string, vw: string) => `${pset} ohne Pset · ${geo} ohne Geometrie · ${vw} ohne VW-Klasse`,
    zaehlung: "Zählung je Klasse",
    stufen: "Vorschläge je Stufe",
    stufenSpalten: { stufe: "Stufe", gruppen: "Gruppen", elemente: "Elemente" },
    /** Die Stufe als Wort in der Tabelle „Vorschläge je Stufe" — eindeutig, anders als die
     *  Plaketten (zweimal „Regel", zweimal „ohne"). */
    stufeName: {
      bestand: "Schon entschieden",
      kennung: "Katalog-ID",
      regel_projekt: "Projektregel",
      hersteller: "Hersteller + Modell",
      vw_klasse: "VW-Klasse",
      regel_buero: "Büroregel",
      llm: "KI (Bibliothek)",
      ohne: "Keine Stufe traf",
      ohne_katalogbezug: "Ohne Katalogbezug",
    } as Record<string, string>,
    stufe: {
      kennung: "Kennung",
      regel_projekt: "Regel",
      hersteller: "Hersteller",
      vw_klasse: "VW",
      regel_buero: "Regel",
      llm: "KI",
      ohne: "ohne",
      ohne_katalogbezug: "ohne",
    } as Record<string, string>,
    stufeHilfe: {
      bestand: "Von Menschen entschieden (bestätigt, ignoriert, Hand oder KI) und unverändert übernommen; Maschinenvorschläge rechnet jeder Import neu",
      kennung: "Katalog-ID im Pset",
      regel_projekt: "Projektregel",
      hersteller: "Hersteller + Modell",
      vw_klasse: "LL-<KAT>-*",
      regel_buero: "Büroregel",
      llm: "Rangfolge aus der Bibliothek",
      ohne: "keine Stufe traf",
      ohne_katalogbezug: "ohne Katalogbezug",
    } as Record<string, string>,
    kiNichtErreichbar: "Die KI war beim Import nicht erreichbar; diese Stufe fehlt.",
    kennungNeuSigniert: (n: number, z: string) =>
      n === 1
        ? `${z} Gruppe trägt jetzt eine Katalog-ID; ihre frühere Bestätigung greift nicht mehr und steht als Vorschlag wieder an.`
        : `${z} Gruppen tragen jetzt eine Katalog-ID; ihre frühere Bestätigung greift nicht mehr und steht als Vorschlag wieder an.`,
    vwKlassen: "VW-Klassen → Kategorie",
    vwSpalten: { klasse: "VW-Klasse", anzahl: "Anz.", kategorie: "Kategorie" },
    liefert: "Was das Modell liefert",
    liefertBasis: (z: string) => `Anteile über ${z} Bauteile (ohne Räume, Öffnungen, Annotationen)`,
    abdeckung: {
      psets: "Psets",
      pset_lilasp: "Pset_LILASp",
      qtos: "Qtos",
      materialien: "Materialien",
      schichten: "Materialschichten",
      klassifikation: "Klassifikation",
      raumpfad: "Raumpfad",
      platzierung: "Platzierung",
      bbox: "Hüllquader",
      vw_klasse: "VW-Klasse",
    } as Record<string, string>,
    vergleich: (datum: string, uhr: string) => (uhr ? `Vergleich mit Import vom ${datum}, ${uhr}` : `Vergleich mit Import vom ${datum}`),
    vergleichOhne: "Vergleich mit dem Vorgänger",
    keinVorgaenger: "Erster Import dieses Projekts — kein Vergleich.",
    diff: {
      neu: "neu",
      entfernt: "entfernt",
      geaendert: "geändert",
      verschoben: "verschoben",
      umbenannt: "umbenannt (Ersatzpaar)",
    } as Record<string, string>,
    teile: "Geänderte Teile",
    teilNamen: {
      psets: "Psets",
      qtos: "Qtos",
      materialien: "Materialien",
      klassifikation: "Klassifikation",
      name: "Name",
      vw_klasse: "VW-Klasse",
      typ_name: "Typname",
    } as Record<string, string>,
    globalIdRest: "der alten GlobalIds sind wieder da.",
    globalIdGut: "Über 0,9: Zuordnungen bleiben elementweise sicher (T10).",
    globalIdWarn: "Elementweise Zuordnungen wurden über Typ und Lage übertragen.",
    /** Mit Zahlen (Prüfung Runde 1): wie viele übertragen, wie viele verloren. */
    elementUebertragen: (um: number, weg: number) =>
      `Elementweise Zuordnungen: ${um.toLocaleString("de-DE")} über Typ und Lage übertragen` +
      (weg ? `, ${weg.toLocaleString("de-DE")} ohne Ziel im neuen Modell.` : "."),
    warnungen: "Warnungen",
    quader: "Hüllquader-Quellen",
    quaderTeile: {
      box: "Box",
      darstellung: "Darstellung",
      teilweise: "teilweise",
      ohne: "ohne",
    } as Record<string, string>,
    quaderTeilweise: (klassen: string) => `teilweise: ${klassen}`,
    luecken: "Lücken im Modell",
    lueckeNamen: {
      ohne_psets: "ohne Psets",
      ohne_geometrie: "ohne Geometrie",
      ohne_vw_klasse: "ohne VW-Klasse",
      ohne_raum: "ohne Raum",
      ohne_platzierung: "ohne Platzierung",
      lesefehler: "Lesefehler",
    } as Record<string, string>,
    imModell: "Im Modell zeigen",
    speicher: (mb: string) => `Speicher max. Auszug ${mb} (Kindprozess)`,
    dauer: (s: string) => `Dauer ${s}`,
    laeuftNoch: "Der Bericht entsteht, sobald der Import fertig ist.",
    leer: "Einen Import links wählen.",
    importFehlt: "Diesen Import gibt es nicht (mehr). Einen Import links wählen.",
  },
} as const;
